import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  ListSubheader,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { CaseStage } from '../../types';

interface StageSelectorProps {
  stages: CaseStage[];
  selectedStage: string;
  onStageChange: (stageId: string) => void;
  isMobile: boolean;
}

export const StageSelector: React.FC<StageSelectorProps> = ({
  stages,
  selectedStage,
  onStageChange,
  isMobile,
}) => {
  // Group stages by category
  const stagesByCategory = stages.reduce((acc, stage) => {
    if (!acc[stage.category]) {
      acc[stage.category] = [];
    }
    acc[stage.category].push(stage);
    return acc;
  }, {} as Record<string, CaseStage[]>);

  // Find current stage and category
  const currentStage = stages.find(s => s.id === selectedStage);
  const currentCategory = currentStage?.category;

  // Get previous and next stages in the same category
  const currentCategoryStages = currentStage 
    ? stagesByCategory[currentStage.category] 
    : [];
  const currentStageIndex = currentCategoryStages.findIndex(s => s.id === selectedStage);
  const previousStage = currentStageIndex > 0 
    ? currentCategoryStages[currentStageIndex - 1] 
    : null;
  const nextStage = currentStageIndex < currentCategoryStages.length - 1 
    ? currentCategoryStages[currentStageIndex + 1] 
    : null;

  return (
    <FormControl sx={{ width: '100%' }}>
      <InputLabel>Case Stage</InputLabel>
      <Select
        value={selectedStage}
        label="Case Stage"
        onChange={(e) => onStageChange(e.target.value)}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: isMobile ? '70vh' : 400,
            },
          },
        }}
      >
        {Object.entries(stagesByCategory).map(([category, stages]) => [
          <ListSubheader 
            key={category}
            sx={{
              bgcolor: 'background.paper',
              lineHeight: '32px',
              fontWeight: 600,
            }}
          >
            {category}
          </ListSubheader>,
          ...stages.map(stage => {
            const isSelected = stage.id === selectedStage;
            const isPrevious = stage.id === previousStage?.id;
            const isNext = stage.id === nextStage?.id;

            return (
              <MenuItem 
                key={stage.id} 
                value={stage.id}
                sx={{ 
                  pl: 4,
                  position: 'relative',
                  ...(isSelected && {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }),
                  ...(isPrevious && {
                    borderBottom: '1px dashed',
                    borderBottomColor: 'divider',
                  }),
                  ...(isNext && {
                    borderTop: !isSelected ? '1px dashed' : 'none',
                    borderTopColor: 'divider',
                  }),
                }}
              >
                <Tooltip 
                  title={stage.description}
                  placement="right"
                  arrow
                >
                  <Box>
                    <Typography 
                      variant="body1"
                      component="span"
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      {stage.name}
                    </Typography>
                    {(isPrevious || isNext) && (
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          ml: 1,
                          color: isSelected 
                            ? 'primary.contrastText' 
                            : 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      >
                        ({isPrevious ? 'Previous' : 'Next'} Stage)
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              </MenuItem>
            );
          })
        ]).flat()}
      </Select>
    </FormControl>
  );
};